"use client";

import { useState, useMemo, useEffect } from "react";
import {
  FileText,
  Upload,
  X,
  ChevronDown,
  ChevronRight,
  Building2,
  Briefcase,
  User,
  FileCode,
  StickyNote,
  File,
  FolderPlus,
} from "lucide-react";
import { Button } from "@/components/ui/forms/button";
import { Card } from "@/components/ui/data-display/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "@/components/ui/layout/dialog";
import { Label } from "@/components/ui/forms/label";
import { Textarea } from "@/components/ui/forms/textarea";
import { Input } from "@/components/ui/forms/input";
import { Badge } from "@/components/ui/data-display/badge";
import { ScrollArea } from "@/components/ui/layout/scroll-area";
import { Separator } from "@/components/ui/layout/separator";
import { DialogHeading, EntitySubtitle } from "@/components/ui/design-system";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

export type SourceType =
  | "RESUME"
  | "NOTE"
  | "RAW"
  | "JOB"
  | "COMPANY"
  | "PROFILE"
  | "FILE";

export type GroupedSource = {
  id: string;
  sourceId?: string;
  type: SourceType;
  title: string;
  description: string;
  details?: Array<{ label: string; value: string }>;
  contentText?: string;
  contentJson?: Record<string, unknown>;
  removable?: boolean;
};

type SourceGroup = {
  type: SourceType;
  label: string;
  icon: typeof Building2;
  sources: GroupedSource[];
};

interface GroupedSourcesPanelProps {
  sources: GroupedSource[];
  isLoading?: boolean;
  onAddSource?: () => void;
  onDeleteSource?: (id: string, sourceId?: string) => Promise<void>;
  onUpdateSource?: (
    id: string,
    updates: Partial<GroupedSource>,
  ) => Promise<void>;
  disabled?: boolean;
  emptyMessage?: string;
}

const sourceTypeConfig: Record<
  SourceType,
  {
    label: string;
    icon: typeof Building2;
    bgColor: string;
    borderColor: string;
    textColor: string;
    groupSurfaceClass: string;
    headerClass: string;
    itemClass: string;
    itemHoverClass: string;
    dividerClass: string;
  }
> = {
  COMPANY: {
    label: "Companies",
    icon: Building2,
    bgColor: "bg-blue-100",
    borderColor: "border-blue-400",
    textColor: "text-blue-950",
    groupSurfaceClass: "bg-blue-50/45 border-blue-200/90",
    headerClass: "bg-blue-100/50 hover:bg-blue-100/65",
    itemClass: "bg-blue-50/25",
    itemHoverClass: "hover:bg-blue-100/40",
    dividerClass: "divide-blue-200/70",
  },
  JOB: {
    label: "Jobs",
    icon: Briefcase,
    bgColor: "bg-emerald-100",
    borderColor: "border-emerald-400",
    textColor: "text-emerald-950",
    groupSurfaceClass: "bg-emerald-50/45 border-emerald-200/90",
    headerClass: "bg-emerald-100/50 hover:bg-emerald-100/65",
    itemClass: "bg-emerald-50/25",
    itemHoverClass: "hover:bg-emerald-100/40",
    dividerClass: "divide-emerald-200/70",
  },
  RESUME: {
    label: "Resumes",
    icon: User,
    bgColor: "bg-violet-100",
    borderColor: "border-violet-400",
    textColor: "text-violet-950",
    groupSurfaceClass: "bg-violet-50/45 border-violet-200/90",
    headerClass: "bg-violet-100/50 hover:bg-violet-100/65",
    itemClass: "bg-violet-50/25",
    itemHoverClass: "hover:bg-violet-100/40",
    dividerClass: "divide-violet-200/70",
  },
  PROFILE: {
    label: "Profiles",
    icon: User,
    bgColor: "bg-indigo-100",
    borderColor: "border-indigo-400",
    textColor: "text-indigo-950",
    groupSurfaceClass: "bg-indigo-50/45 border-indigo-200/90",
    headerClass: "bg-indigo-100/50 hover:bg-indigo-100/65",
    itemClass: "bg-indigo-50/25",
    itemHoverClass: "hover:bg-indigo-100/40",
    dividerClass: "divide-indigo-200/70",
  },
  NOTE: {
    label: "Notes",
    icon: StickyNote,
    bgColor: "bg-amber-100",
    borderColor: "border-amber-400",
    textColor: "text-amber-950",
    groupSurfaceClass: "bg-amber-50/45 border-amber-200/90",
    headerClass: "bg-amber-100/50 hover:bg-amber-100/65",
    itemClass: "bg-amber-50/25",
    itemHoverClass: "hover:bg-amber-100/40",
    dividerClass: "divide-amber-200/70",
  },
  FILE: {
    label: "Files",
    icon: File,
    bgColor: "bg-slate-100",
    borderColor: "border-slate-400",
    textColor: "text-slate-950",
    groupSurfaceClass: "bg-slate-50/55 border-slate-200/90",
    headerClass: "bg-slate-100/50 hover:bg-slate-100/65",
    itemClass: "bg-slate-50/25",
    itemHoverClass: "hover:bg-slate-100/40",
    dividerClass: "divide-slate-200/70",
  },
  RAW: {
    label: "Other",
    icon: FileCode,
    bgColor: "bg-gray-100",
    borderColor: "border-gray-400",
    textColor: "text-gray-950",
    groupSurfaceClass: "bg-gray-50/55 border-gray-200/90",
    headerClass: "bg-gray-100/50 hover:bg-gray-100/65",
    itemClass: "bg-gray-50/25",
    itemHoverClass: "hover:bg-gray-100/40",
    dividerClass: "divide-gray-200/70",
  },
};

const getJsonStringField = (
  value: unknown,
  options?: { trim?: boolean },
): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const normalized = options?.trim ? value.trim() : value;
  return normalized.length > 0 ? normalized : undefined;
};

const getFirstLine = (value: string): string | undefined => {
  const line = value
    .split("\n")
    .map((part) => part.trim())
    .find((part) => part.length > 0);
  return line || undefined;
};

const dialogFieldLabelClassName =
  "flex items-center gap-2 text-label text-foreground";
const dialogSectionClassName = "border border-border/60 bg-muted/20";
const dialogSectionHeadingClassName = "text-overline text-muted-foreground";

export function GroupedSourcesPanel({
  sources,
  isLoading = false,
  onAddSource,
  onDeleteSource,
  onUpdateSource,
  disabled = false,
  emptyMessage = "Upload a resume, paste a note, or drop a job link to sharpen this report.",
}: GroupedSourcesPanelProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<SourceType>>(
    new Set(Object.keys(sourceTypeConfig) as SourceType[]),
  );
  const [selectedSource, setSelectedSource] = useState<GroupedSource | null>(
    null,
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmSource, setDeleteConfirmSource] =
    useState<GroupedSource | null>(null);
  const [optimisticallyRemovedSourceIds, setOptimisticallyRemovedSourceIds] =
    useState<Set<string>>(new Set());
  const [editedTitle, setEditedTitle] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [editedContentText, setEditedContentText] = useState("");

  const selectedProfileJson =
    selectedSource?.type === "PROFILE" ? selectedSource.contentJson : undefined;
  const selectedProfileLocation = selectedProfileJson
    ? getJsonStringField(selectedProfileJson.location, { trim: true })
    : undefined;
  const selectedProfileRemoteOpenness = selectedProfileJson
    ? getJsonStringField(selectedProfileJson.remoteOpenness, { trim: true })
    : undefined;
  const selectedProfileAdditionalTitles = selectedProfileJson
    ? getJsonStringField(selectedProfileJson.additionalTitles)
    : undefined;
  const selectedProfileAdditionalTitleParts = selectedProfileAdditionalTitles
    ? selectedProfileAdditionalTitles
        .split(",")
        .map((title) => title.trim())
        .filter((title) => title.length > 0)
    : [];
  const selectedProfileSkills = selectedProfileJson
    ? getJsonStringField(selectedProfileJson.skills, { trim: true })
    : undefined;
  const selectedProfileHasStructuredSummary = Boolean(
    selectedProfileLocation ||
    selectedProfileRemoteOpenness ||
    selectedProfileAdditionalTitleParts.length > 0 ||
    selectedProfileSkills,
  );
  const selectedProfileTextPreview =
    selectedSource?.type === "PROFILE" && selectedSource.contentText
      ? getFirstLine(selectedSource.contentText)
      : undefined;
  const selectedSourceConfig = selectedSource
    ? sourceTypeConfig[selectedSource.type]
    : undefined;

  const visibleSources = useMemo(
    () =>
      sources.filter(
        (source) => !optimisticallyRemovedSourceIds.has(source.id),
      ),
    [optimisticallyRemovedSourceIds, sources],
  );

  useEffect(() => {
    setOptimisticallyRemovedSourceIds((prev) => {
      if (prev.size === 0) {
        return prev;
      }
      const sourceIds = new Set(sources.map((source) => source.id));
      const next = new Set(Array.from(prev).filter((id) => sourceIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [sources]);

  useEffect(() => {
    if (!deleteConfirmSource) {
      return;
    }
    if (
      !visibleSources.some((source) => source.id === deleteConfirmSource.id)
    ) {
      setDeleteConfirmSource(null);
    }
  }, [deleteConfirmSource, visibleSources]);

  const groupedSources = useMemo<SourceGroup[]>(() => {
    const groups: SourceGroup[] = [];
    const sourcesByType = visibleSources.reduce(
      (acc, source) => {
        if (!acc[source.type]) {
          acc[source.type] = [];
        }
        acc[source.type].push(source);
        return acc;
      },
      {} as Record<SourceType, GroupedSource[]>,
    );

    // Create groups only for types that have sources
    Object.entries(sourcesByType).forEach(([type, typeSources]) => {
      const config = sourceTypeConfig[type as SourceType];
      if (config && typeSources.length > 0) {
        groups.push({
          type: type as SourceType,
          label: config.label,
          icon: config.icon,
          sources: typeSources,
        });
      }
    });

    // Sort groups by a preferred order
    const order: SourceType[] = [
      "RESUME",
      "PROFILE",
      "COMPANY",
      "JOB",
      "NOTE",
      "FILE",
      "RAW",
    ];
    groups.sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));

    return groups;
  }, [visibleSources]);

  const toggleGroup = (type: SourceType) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const handleSourceClick = (source: GroupedSource) => {
    setSelectedSource(source);
    setEditedTitle(source.title || "");
    setEditedDescription(source.description || "");
    setEditedContentText(source.contentText || "");
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedSource(null);
    setEditedTitle("");
    setEditedDescription("");
    setEditedContentText("");
  };

  const handleDelete = async () => {
    if (!selectedSource || !onDeleteSource) return;

    setIsDeleting(true);
    try {
      await onDeleteSource(selectedSource.id, selectedSource.sourceId);
      handleCloseDialog();
    } catch (error) {
      console.error("Failed to delete source:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleQuickDelete = (source: GroupedSource, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!onDeleteSource) return;

    // Show confirmation
    if (!deleteConfirmSource || deleteConfirmSource.id !== source.id) {
      setDeleteConfirmSource(source);
      return;
    }

    // Already confirmed, remove immediately and run delete in background
    setDeleteConfirmSource(null);
    setOptimisticallyRemovedSourceIds((prev) => new Set(prev).add(source.id));
    void onDeleteSource(source.id, source.sourceId).catch((error) => {
      console.error("Failed to delete source:", error);
      setOptimisticallyRemovedSourceIds((prev) => {
        const next = new Set(prev);
        next.delete(source.id);
        return next;
      });
    });
  };

  const handleSave = async () => {
    if (!selectedSource || !onUpdateSource) return;

    try {
      await onUpdateSource(selectedSource.id, {
        title: editedTitle,
        description: editedDescription,
        contentText: editedContentText,
      });
      handleCloseDialog();
    } catch (error) {
      console.error("Failed to update source:", error);
    }
  };

  if (isLoading && visibleSources.length === 0) {
    return (
      <div className="px-4 py-4">
        <div className="text-caption text-muted-foreground flex items-center gap-2">
          <span className="inline-block h-3 w-3 animate-spin rounded-full border border-muted-foreground border-t-transparent" />
          Loading sources...
        </div>
      </div>
    );
  }

  if (visibleSources.length === 0) {
    return (
      <>
        <div className="flex-1 px-4 py-4">
          <div className="border-l border-primary/20 pl-3 text-caption">
            <div className="flex items-start gap-2">
              <Upload className="h-3.5 w-3.5 text-primary/60 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-foreground text-caption mb-0.5">
                  Add your first source
                </p>
                <p className="text-2xs text-muted-foreground">{emptyMessage}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Add Source Button */}
        {onAddSource && (
          <div className="flex-shrink-0 border-t border-border/60 px-4 py-3">
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-full border text-caption"
              onClick={onAddSource}
              disabled={disabled}
            >
              <FolderPlus className="h-3 w-3 mr-1.5" />
              Add Source
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <div className="space-y-2">
          {groupedSources.map((group) => {
            const isExpanded = expandedGroups.has(group.type);
            const Icon = group.icon;
            const config = sourceTypeConfig[group.type];

            return (
              <Card
                key={group.type}
                brutalist={false}
                className={cn(
                  "max-w-none overflow-hidden",
                  config.groupSurfaceClass,
                )}
              >
                {/* Group Header */}
                <button
                  type="button"
                  onClick={() => toggleGroup(group.type)}
                  className={cn(
                    "flex w-full items-center justify-between border-b border-border/60 px-2.5 py-2 text-foreground transition-colors",
                    config.headerClass,
                  )}
                >
                  <div className="flex items-center gap-1.5">
                    {isExpanded ? (
                      <ChevronDown className="h-3 w-3 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                    )}
                    <span
                      className={cn(
                        "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-sm",
                        config.bgColor,
                        config.textColor,
                      )}
                    >
                      <Icon className="h-3 w-3" />
                    </span>
                    <EntitySubtitle
                      as="span"
                      className="text-caption font-semibold"
                    >
                      {group.label}
                    </EntitySubtitle>
                  </div>
                  <Badge
                    variant="outline"
                    className={cn(
                      "h-4 px-1 py-0 text-overline",
                      config.borderColor,
                      config.textColor,
                    )}
                  >
                    {group.sources.length}
                  </Badge>
                </button>

                {/* Group Items */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      className="overflow-hidden"
                    >
                      <div className={cn("divide-y", config.dividerClass)}>
                        {group.sources.map((source) => {
                          const profileJson =
                            source.type === "PROFILE"
                              ? source.contentJson
                              : undefined;
                          const profileLocation = profileJson
                            ? getJsonStringField(profileJson.location, {
                                trim: true,
                              })
                            : undefined;
                          const profileRemoteOpenness = profileJson
                            ? getJsonStringField(profileJson.remoteOpenness, {
                                trim: true,
                              })
                            : undefined;
                          const profileAdditionalTitles = profileJson
                            ? getJsonStringField(profileJson.additionalTitles)
                            : undefined;
                          const profileAdditionalTitleParts =
                            profileAdditionalTitles
                              ? profileAdditionalTitles
                                  .split(",")
                                  .map((title) => title.trim())
                                  .filter((title) => title.length > 0)
                              : [];
                          const hasProfileSummary = Boolean(
                            profileLocation ||
                            profileRemoteOpenness ||
                            profileAdditionalTitleParts.length > 0,
                          );
                          const profileTextPreview = source.contentText
                            ? getFirstLine(source.contentText)
                            : undefined;

                          const isConfirmingDelete =
                            deleteConfirmSource?.id === source.id;
                          return (
                            <div
                              key={source.id}
                              className="relative group/item"
                            >
                              <button
                                type="button"
                                onClick={() => handleSourceClick(source)}
                                className={cn(
                                  "relative w-full px-2.5 py-2 pr-7 text-left text-foreground transition-colors",
                                  config.itemClass,
                                  config.itemHoverClass,
                                )}
                              >
                                <EntitySubtitle
                                  as="div"
                                  className="line-clamp-2 text-caption font-medium leading-tight text-current"
                                >
                                  {source.title}
                                </EntitySubtitle>
                                {source.type === "PROFILE" &&
                                profileJson &&
                                hasProfileSummary ? (
                                  <div className="text-2xs text-muted-foreground mt-0.5 space-y-0.5">
                                    {profileLocation && (
                                      <div className="line-clamp-1">
                                        📍 {profileLocation}
                                      </div>
                                    )}
                                    {profileRemoteOpenness && (
                                      <div className="line-clamp-1">
                                        🏢 {profileRemoteOpenness}
                                      </div>
                                    )}
                                    {profileAdditionalTitleParts.length > 0 && (
                                      <div className="line-clamp-1">
                                        💼 {profileAdditionalTitleParts[0]}
                                        {profileAdditionalTitleParts.length > 1
                                          ? "..."
                                          : ""}
                                      </div>
                                    )}
                                  </div>
                                ) : source.type === "PROFILE" ? (
                                  (source.description ||
                                    profileTextPreview) && (
                                    <div className="mt-0.5 line-clamp-1 text-caption font-normal leading-tight text-muted-foreground/90">
                                      {source.description || profileTextPreview}
                                    </div>
                                  )
                                ) : source.description ? (
                                  <div className="mt-0.5 line-clamp-1 text-caption font-normal leading-tight text-muted-foreground/90">
                                    {source.description}
                                  </div>
                                ) : null}
                              </button>
                              {source.removable !== false && onDeleteSource && (
                                <button
                                  type="button"
                                  onClick={(e) => handleQuickDelete(source, e)}
                                  className={`absolute top-1 right-1 min-h-[22px] rounded px-2 text-overline transition-all ${
                                    isConfirmingDelete
                                      ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      : "opacity-70 hover:opacity-100 hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                                  }`}
                                  title={
                                    isConfirmingDelete
                                      ? "Click again to confirm delete"
                                      : "Delete source"
                                  }
                                >
                                  {isConfirmingDelete ? (
                                    "Confirm"
                                  ) : (
                                    <X className="h-3 w-3" />
                                  )}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Add Source Button */}
      {onAddSource && (
        <div className="flex-shrink-0 border-t border-border/60 px-4 py-3">
          <Button
            size="sm"
            variant="outline"
            className="h-8 w-full border bg-card text-overline text-foreground"
            onClick={onAddSource}
            disabled={disabled}
          >
            <FolderPlus className="h-3 w-3 mr-1.5" />
            Add Source
          </Button>
        </div>
      )}

      {/* Source Preview/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeading
            leading={
              <div className="flex size-11 shrink-0 items-center justify-center border border-border/60 bg-muted/30 text-primary">
                {selectedSourceConfig ? (
                  <selectedSourceConfig.icon className="size-5" />
                ) : (
                  <FileText className="size-5" />
                )}
              </div>
            }
            title={
              <>
                Source Details
                {selectedSourceConfig && (
                  <Badge variant="outline" className="ml-2 inline-flex font-semibold align-middle">
                    {selectedSourceConfig.label}
                  </Badge>
                )}
              </>
            }
            description="View and edit source information."
          />

          {selectedSource && (
            <ScrollArea className="flex-1">
              <div className="flex flex-col gap-6 px-6 py-6">
                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="source-title"
                    className={dialogFieldLabelClassName}
                  >
                    <FileText className="size-4 text-primary" />
                    Title
                  </Label>
                  <Input
                    id="source-title"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    placeholder="Enter source title..."
                    className="h-11 text-body"
                    disabled={!onUpdateSource}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="source-description"
                    className={dialogFieldLabelClassName}
                  >
                    <FileText className="size-4 text-primary" />
                    Description
                  </Label>
                  <Textarea
                    id="source-description"
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    placeholder="Enter a brief description..."
                    className="min-h-[120px] resize-y text-body"
                    rows={4}
                    disabled={!onUpdateSource}
                  />
                </div>

                {(selectedSource.contentText ||
                  selectedSource.type === "NOTE") && (
                  <div className="flex flex-col gap-2">
                    <Label
                      htmlFor="source-content"
                      className={dialogFieldLabelClassName}
                    >
                      <FileCode className="size-4 text-primary" />
                      Content
                    </Label>
                    <Textarea
                      id="source-content"
                      value={editedContentText}
                      onChange={(e) => setEditedContentText(e.target.value)}
                      placeholder="Enter content..."
                      className="min-h-[260px] resize-y font-mono text-body-sm whitespace-pre-wrap break-words"
                      rows={10}
                      disabled={!onUpdateSource}
                    />
                  </div>
                )}

                {selectedSource.details &&
                  selectedSource.details.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <div className={dialogFieldLabelClassName}>
                        <StickyNote className="size-4 text-primary" />
                        Additional Information
                      </div>
                      <div
                        className={cn(
                          dialogSectionClassName,
                          "flex flex-col p-4",
                        )}
                      >
                        {selectedSource.details.map((detail, idx) => (
                          <div
                            key={`${detail.label}-${idx}`}
                            className="flex flex-col gap-3"
                          >
                            {idx > 0 && <Separator />}
                            <dl className="grid gap-2 sm:grid-cols-[160px_minmax(0,1fr)] sm:gap-4">
                              <dt className={dialogSectionHeadingClassName}>
                                {detail.label}
                              </dt>
                              <dd className="min-w-0 text-body-sm text-foreground whitespace-pre-wrap break-words">
                                {detail.value}
                              </dd>
                            </dl>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {selectedSource.type === "PROFILE" &&
                  selectedSource.contentJson && (
                    <div className="flex flex-col gap-2">
                      <div className={dialogFieldLabelClassName}>
                        <User className="size-4 text-primary" />
                        Profile Details
                      </div>
                      <div
                        className={cn(
                          dialogSectionClassName,
                          "flex flex-col gap-4 p-4",
                        )}
                      >
                        {selectedProfileAdditionalTitleParts.length > 0 && (
                          <div className="flex flex-col gap-2">
                            <div className={dialogSectionHeadingClassName}>
                              Interested Job Titles
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {selectedProfileAdditionalTitleParts.map(
                                (title, idx) => (
                                  <Badge
                                    key={`${title}-${idx}`}
                                    variant="secondary"
                                    className="px-2.5 py-1 normal-case text-caption"
                                  >
                                    {title}
                                  </Badge>
                                ),
                              )}
                            </div>
                          </div>
                        )}

                        {selectedProfileSkills && (
                          <div className="flex flex-col gap-2">
                            <div className={dialogSectionHeadingClassName}>
                              Key Skills
                            </div>
                            <p className="text-body text-foreground whitespace-pre-wrap break-words">
                              {selectedProfileSkills}
                            </p>
                          </div>
                        )}

                        {(selectedProfileLocation ||
                          selectedProfileRemoteOpenness) && (
                          <div className="grid gap-4 sm:grid-cols-2">
                            {selectedProfileLocation && (
                              <div className="flex flex-col gap-2">
                                <div className={dialogSectionHeadingClassName}>
                                  Location
                                </div>
                                <p className="text-body text-foreground">
                                  {selectedProfileLocation}
                                </p>
                              </div>
                            )}
                            {selectedProfileRemoteOpenness && (
                              <div className="flex flex-col gap-2">
                                <div className={dialogSectionHeadingClassName}>
                                  Remote Work Preference
                                </div>
                                <div>
                                  <Badge
                                    variant="outline"
                                    className="px-2.5 py-1 capitalize"
                                  >
                                    {selectedProfileRemoteOpenness}
                                  </Badge>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {selectedProfileTextPreview && (
                          <>
                            {selectedProfileHasStructuredSummary && (
                              <Separator />
                            )}
                            <div className="flex flex-col gap-2">
                              <div className={dialogSectionHeadingClassName}>
                                Profile Source Text
                              </div>
                              <p className="text-body text-foreground whitespace-pre-wrap break-words">
                                {selectedSource.contentText}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                {selectedSource.type !== "PROFILE" &&
                  selectedSource.contentJson &&
                  Object.keys(selectedSource.contentJson).length > 0 && (
                    <div className="flex flex-col gap-2">
                      <div className={dialogFieldLabelClassName}>
                        <FileCode className="size-4 text-primary" />
                        Data
                      </div>
                      <div
                        className={cn(
                          dialogSectionClassName,
                          "overflow-hidden",
                        )}
                      >
                        <ScrollArea className="max-h-72">
                          <pre className="p-4 text-caption text-foreground/90 whitespace-pre-wrap break-words">
                            {JSON.stringify(
                              selectedSource.contentJson,
                              null,
                              2,
                            )}
                          </pre>
                        </ScrollArea>
                      </div>
                    </div>
                  )}
              </div>
            </ScrollArea>
          )}

          <DialogFooter className="border-t border-border px-6 py-4">
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                {selectedSource &&
                  selectedSource.removable !== false &&
                  onDeleteSource && (
                    <Button
                      variant="destructive"
                      onClick={handleDelete}
                      disabled={isDeleting}
                    >
                      {isDeleting ? (
                        <>
                          <span className="inline-block size-4 animate-spin rounded-full border border-current border-t-transparent" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <X data-icon="inline-start" />
                          Delete Source
                        </>
                      )}
                    </Button>
                  )}
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <Button variant="outline" onClick={handleCloseDialog}>
                  {onUpdateSource ? "Cancel" : "Close"}
                </Button>
                {onUpdateSource && (
                  <Button onClick={handleSave}>Save Changes</Button>
                )}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
